import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../contexts/AuthContext';
import { Eye, EyeOff, Building2, ShieldCheck, Receipt, Wrench } from 'lucide-react';

const HIGHLIGHTS = [
  { icon: Receipt, title: 'Rent & statements', copy: 'Invoices, receipts and balances in one place.' },
  { icon: Wrench, title: 'Maintenance', copy: 'Report an issue and follow it through to done.' },
  { icon: ShieldCheck, title: 'Secure payments', copy: 'Pay by M-Pesa or bank; every payment reconciled.' },
];

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const { register, handleSubmit, formState: { errors } } = useForm();

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      const result = await login(data);
      if (result.success) navigate('/dashboard');
    } catch (error) {
      console.error('Login error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fieldClass = (hasError) =>
    `block w-full rounded-xl border px-4 py-3 text-base text-neutral-900 placeholder-neutral-400
     shadow-sm transition focus:outline-none focus:ring-4
     ${hasError
       ? 'border-danger-400 focus:border-danger-500 focus:ring-danger-100'
       : 'border-neutral-300 focus:border-primary-600 focus:ring-primary-100'}`;

  return (
    <div className="min-h-screen bg-neutral-50 lg:grid lg:grid-cols-2">
      {/* Brand panel — hidden on small screens so the form leads on mobile */}
      <aside className="relative hidden lg:flex flex-col justify-between bg-primary-800 px-12 py-14 text-white overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-primary-700/60 blur-3xl"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -left-20 bottom-0 h-72 w-72 rounded-full bg-primary-600/40 blur-3xl"
        />

        <div className="relative flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/25">
            <Building2 className="h-6 w-6" />
          </span>
          <span className="text-lg font-semibold tracking-tight">Roth Property</span>
        </div>

        <div className="relative max-w-md">
          <h1 className="text-4xl font-semibold leading-tight tracking-tight">
            Everything about your property, in one place.
          </h1>
          <p className="mt-4 text-primary-100">
            Rent, utilities, maintenance and payments — for landlords, owners and tenants.
          </p>

          <ul className="mt-10 space-y-5">
            {HIGHLIGHTS.map(({ icon: Icon, title, copy }) => (
              <li key={title} className="flex gap-4">
                <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/10 ring-1 ring-white/20">
                  <Icon className="h-4 w-4" aria-hidden="true" />
                </span>
                <div>
                  <p className="font-medium">{title}</p>
                  <p className="text-sm text-primary-100/90">{copy}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <p className="relative text-sm text-primary-200">Roth Property Management · Nairobi, Kenya</p>
      </aside>

      {/* Form panel */}
      <main className="flex min-h-screen items-center justify-center px-5 py-12 sm:px-8 lg:min-h-0">
        <div className="w-full max-w-sm">
          {/* Mobile brand lockup */}
          <div className="mb-10 flex items-center gap-3 lg:hidden">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-700 text-white">
              <Building2 className="h-6 w-6" />
            </span>
            <span className="text-lg font-semibold tracking-tight text-neutral-900">Roth Property</span>
          </div>

          <h2 className="text-2xl font-semibold tracking-tight text-neutral-900 sm:text-3xl">Sign in</h2>
          <p className="mt-2 text-neutral-600">
            Use the username and password you were given.
          </p>

          <form className="mt-8 space-y-5" onSubmit={handleSubmit(onSubmit)} noValidate>
            <div>
              <label htmlFor="username" className="mb-1.5 block text-sm font-medium text-neutral-800">
                Username or email
              </label>
              <input
                id="username"
                type="text"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck="false"
                aria-invalid={errors.username ? 'true' : 'false'}
                aria-describedby={errors.username ? 'username-error' : undefined}
                {...register('username', { required: 'Enter your username or email' })}
                className={fieldClass(errors.username)}
              />
              {errors.username && (
                <p id="username-error" role="alert" className="mt-1.5 text-sm text-danger-600">
                  {errors.username.message}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="mb-1.5 block text-sm font-medium text-neutral-800">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  aria-invalid={errors.password ? 'true' : 'false'}
                  aria-describedby={errors.password ? 'password-error' : undefined}
                  {...register('password', { required: 'Enter your password' })}
                  className={`${fieldClass(errors.password)} pr-12`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                  className="absolute inset-y-0 right-0 flex w-12 items-center justify-center rounded-r-xl text-neutral-400 transition hover:text-neutral-700 focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-100"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {errors.password && (
                <p id="password-error" role="alert" className="mt-1.5 text-sm text-danger-600">
                  {errors.password.message}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary-700 px-4 py-3 text-base font-medium text-white shadow-sm transition hover:bg-primary-800 focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-200 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isLoading && (
                <span
                  aria-hidden="true"
                  className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"
                />
              )}
              {isLoading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>

          <p className="mt-8 text-sm text-neutral-500">
            Forgotten your password? Contact your property manager and they will issue a new one.
          </p>
        </div>
      </main>
    </div>
  );
};

export default Login;
